import React, { useState, useRef, useEffect } from 'react';
import './App.css';

function App() {
  const viewerRef = useRef(null);
  const containerRef = useRef(null);
  const panoramaContainerRef = useRef(null);
  const [isAutoRotate, setIsAutoRotate] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isViewerActive, setIsViewerActive] = useState(false);

  // 初始化 Pannellum
  useEffect(() => {
    if (!isViewerActive) return;

    // 動態加載 Pannellum 的 CSS 和 JS
    const loadPannellum = async () => {
      // 加載 CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css';
      document.head.appendChild(link);

      // 加載 JS
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js';
      script.async = true;
      
      script.onload = () => {
        // 初始化查看器
        if (window.pannellum && panoramaContainerRef.current) {
          viewerRef.current = window.pannellum.viewer(panoramaContainerRef.current, {
            type: 'equirectangular',
            panorama: '/core_interior.webp',
            autoLoad: true,
            showControls: false,
            mouseZoom: true,
            draggable: true,
            pitch: 0,
            yaw: 0,
            hfov: 100,
            minHfov: 50,
            maxHfov: 120,
          });
        }
      };
      
      document.body.appendChild(script);

      return () => {
        document.head.removeChild(link);
        document.body.removeChild(script);
      };
    };

    loadPannellum();
  }, [isViewerActive]);

  // 啟動查看器
  const activateViewer = () => {
    setIsViewerActive(true);
  };

  // 處理全螢幕
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // 監聽全螢幕變化
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // 放大
  const zoomIn = () => {
    if (viewerRef.current) {
      const currentHfov = viewerRef.current.getHfov();
      const newHfov = Math.max(currentHfov - 20, 50);
      viewerRef.current.setHfov(newHfov, 300);
    }
  };

  // 縮小
  const zoomOut = () => {
    if (viewerRef.current) {
      const currentHfov = viewerRef.current.getHfov();
      const newHfov = Math.min(currentHfov + 20, 120);
      viewerRef.current.setHfov(newHfov, 300);
    }
  };

  // 切換自動旋轉
  const toggleAutoRotate = () => {
    if (viewerRef.current) {
      if (isAutoRotate) {
        viewerRef.current.stopAutoRotate();
      } else {
        viewerRef.current.startAutoRotate(2);
      }
      setIsAutoRotate(!isAutoRotate);
    }
  };

  return (
    <div className="app-container" ref={containerRef}>
      {/* 標題區域 - 移到外部 */}
      <div className="viewer-header-external">
        <h1 className="viewer-title">360度賞車</h1>
        <div className="viewer-tabs">
          <button className="tab-btn">外觀</button>
          <button className="tab-btn active">內裝</button>
        </div>
      </div>

      <div className="panorama-viewer">
        {!isViewerActive ? (
          /* 預覽圖 */
          <div className="preview-container" onClick={activateViewer}>
            <img 
              src="/core_interior_static.webp" 
              alt="車內預覽" 
              className="preview-image"
            />
            <div className="preview-overlay">
              <div className="preview-icon">
                <img 
                  src="/interior-text.png" 
                  alt="360° 旋轉觀看" 
                  style={{ width: '100%', height: 'auto' }}
                />
              </div>
              <p className="preview-text">旋轉觀看車內環景</p>
            </div>
          </div>
        ) : (
          /* 全景查看器 */
          <>
            <div 
              ref={panoramaContainerRef} 
              className="pannellum-container"
              style={{ width: '100%', height: '100%' }}
            />

            {/* 控制面板 */}
            <div className="controls-container">
              {/* 縮放控制 */}
              <div className="control-group zoom-controls">
                <button 
                  className="control-btn" 
                  onClick={zoomIn}
                  title="放大"
                  aria-label="放大"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                <button 
                  className="control-btn" 
                  onClick={zoomOut}
                  title="縮小"
                  aria-label="縮小"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* 自動旋轉控制 */}
              <div className="control-group">
                <button 
                  className={`control-btn ${isAutoRotate ? 'active' : ''}`}
                  onClick={toggleAutoRotate}
                  title="自動旋轉"
                  aria-label="自動旋轉"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M21.5 12C21.5 7.313 17.687 3.5 13 3.5C9.36 3.5 6.273 5.785 4.976 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M2.5 12C2.5 16.687 6.313 20.5 11 20.5C14.64 20.5 17.727 18.215 19.024 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M2 9L5 9L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 15L19 15L19 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              {/* 全螢幕控制 */}
              <div className="control-group">
                <button 
                  className={`control-btn ${isFullscreen ? 'active' : ''}`}
                  onClick={toggleFullscreen}
                  title="全螢幕"
                  aria-label="全螢幕"
                >
                  {!isFullscreen ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M8 3V6M8 6V9M8 6H5M8 6H11M16 3V6M16 6V9M16 6H13M16 6H19M8 21V18M8 18V15M8 18H5M8 18H11M16 21V18M16 18V15M16 18H19M16 18H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* 提示文字 */}
            <div className="hint-text">
              拖曳滑鼠以旋轉視角 | 滾輪縮放
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default App;
